
import { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { simulatorRoutes, departures, vehicleTypes, supplements } from '../../../mocks/pricingData';

export default function PriceSimulator() {
  const [selectedDeparture, setSelectedDeparture] = useState('');
  const [selectedDestination, setSelectedDestination] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState('berline');
  const [selectedSupplements, setSelectedSupplements] = useState<string[]>([]);
  const [tripType, setTripType] = useState<'aller-simple' | 'aller-retour'>('aller-simple');
  const [showResult, setShowResult] = useState(false);

  const availableDestinations = useMemo(() => {
    if (!selectedDeparture) return [];
    const dests = simulatorRoutes
      .filter((r) => r.departure === selectedDeparture)
      .map((r) => r.destination);
    return [...new Set(dests)];
  }, [selectedDeparture]);

  useEffect(() => {
    setSelectedDestination('');
    setShowResult(false);
  }, [selectedDeparture]);

  useEffect(() => {
    setShowResult(false);
  }, [selectedDestination, selectedVehicle, tripType, selectedSupplements]);

  const matchedRoute = useMemo(() => {
    if (!selectedDeparture || !selectedDestination) return null;
    return simulatorRoutes.find(
      (r) => r.departure === selectedDeparture && r.destination === selectedDestination,
    ) || null;
  }, [selectedDeparture, selectedDestination]);

  const totalPrice = useMemo(() => {
    if (!matchedRoute) return 0;
    const basePrice = selectedVehicle === 'berline' ? matchedRoute.priceBerline : matchedRoute.priceVan;
    const suppTotal = selectedSupplements.reduce((sum, id) => {
      const s = supplements.find((sup) => sup.id === id);
      return sum + (s ? s.price : 0);
    }, 0);
    const subtotal = basePrice + suppTotal;
    if (tripType === 'aller-retour') {
      return Math.round(subtotal * 2 * 0.95);
    }
    return subtotal;
  }, [matchedRoute, selectedVehicle, selectedSupplements, tripType]);

  const toggleSupplement = (id: string) => {
    setSelectedSupplements((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );
  };

  const handleSimulate = () => {
    if (matchedRoute) {
      setShowResult(true);
    }
  };

  const generateWhatsAppLink = () => {
    const vehicle = vehicleTypes.find((v) => v.id === selectedVehicle);
    const suppLabels = selectedSupplements
      .map((id) => supplements.find((s) => s.id === id)?.label)
      .filter(Boolean)
      .join(', ');
    const msg = `🚗 *Demande de réservation*\n\n📍 Départ : ${selectedDeparture}\n📍 Destination : ${selectedDestination}\n🚙 Véhicule : ${vehicle?.name} (${vehicle?.type})\n🔄 Trajet : ${tripType === 'aller-retour' ? 'Aller-retour' : 'Aller simple'}${suppLabels ? `\n✨ Options : ${suppLabels}` : ''}\n💰 Prix estimé : ${totalPrice} €`;
    return `https://wa.me/33605606006?text=${encodeURIComponent(msg)}`;
  };

  const currentVehicle = vehicleTypes.find((v) => v.id === selectedVehicle);

  return (
    <section className="py-20 bg-gradient-to-b from-slate-50 to-white" id="simulateur">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-voyage-gold/10 rounded-full mb-4">
            <i className="ri-calculator-line text-voyage-gold"></i>
            <span className="font-inter text-sm font-semibold text-voyage-gold">Simulateur interactif</span>
          </div>
          <h2 className="font-playfair text-3xl md:text-4xl font-bold text-night-blue mb-3">
            Calculez votre tarif en temps réel
          </h2>
          <p className="font-inter text-slate-600 max-w-2xl mx-auto">
            Sélectionnez votre trajet, votre véhicule et vos options pour obtenir un prix instantané
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white rounded-3xl shadow-2xl border border-slate-100 overflow-hidden"
        >
          <div className="p-6 md:p-10">
            {/* Étape 1 : Trajet */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-8 bg-voyage-gold rounded-full flex items-center justify-center text-white font-inter font-bold text-sm">1</div>
                <h3 className="font-playfair text-xl font-bold text-night-blue">Votre trajet</h3>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block font-inter text-sm font-semibold text-night-blue mb-2">
                    <i className="ri-map-pin-line text-voyage-gold mr-1"></i> Lieu de départ
                  </label>
                  <div className="relative">
                    <select
                      value={selectedDeparture}
                      onChange={(e) => setSelectedDeparture(e.target.value)}
                      className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl font-inter text-sm text-night-blue focus:outline-none focus:ring-2 focus:ring-voyage-gold/50 focus:border-voyage-gold transition-all appearance-none cursor-pointer"
                    >
                      <option value="">Choisir un point de départ</option>
                      {departures.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"></i>
                  </div>
                </div>

                <div>
                  <label className="block font-inter text-sm font-semibold text-night-blue mb-2">
                    <i className="ri-flag-line text-voyage-gold mr-1"></i> Destination
                  </label>
                  <div className="relative">
                    <select
                      value={selectedDestination}
                      onChange={(e) => setSelectedDestination(e.target.value)}
                      disabled={!selectedDeparture}
                      className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl font-inter text-sm text-night-blue focus:outline-none focus:ring-2 focus:ring-voyage-gold/50 focus:border-voyage-gold transition-all appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="">{selectedDeparture ? 'Choisir une destination' : 'Sélectionnez d\'abord un départ'}</option>
                      {availableDestinations.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"></i>
                  </div>
                </div>
              </div>

              {/* Type de trajet */}
              <div className="mt-5">
                <div className="flex gap-3">
                  <button
                    onClick={() => setTripType('aller-simple')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-inter text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                      tripType === 'aller-simple'
                        ? 'bg-night-blue text-white shadow-lg'
                        : 'bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100'
                    }`}
                  >
                    <i className="ri-arrow-right-line"></i>
                    Aller simple
                  </button>
                  <button
                    onClick={() => setTripType('aller-retour')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-inter text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                      tripType === 'aller-retour'
                        ? 'bg-night-blue text-white shadow-lg'
                        : 'bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100'
                    }`}
                  >
                    <i className="ri-arrow-left-right-line"></i>
                    Aller-retour (-5%)
                  </button>
                </div>
              </div>
            </div>

            {/* Étape 2 : Véhicule */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-8 bg-voyage-gold rounded-full flex items-center justify-center text-white font-inter font-bold text-sm">2</div>
                <h3 className="font-playfair text-xl font-bold text-night-blue">Votre véhicule</h3>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {vehicleTypes.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setSelectedVehicle(v.id)}
                    className={`relative flex flex-col items-center p-5 rounded-2xl border-2 transition-all cursor-pointer group ${
                      selectedVehicle === v.id
                        ? 'border-voyage-gold bg-voyage-gold/5 shadow-lg'
                        : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-md'
                    }`}
                  >
                    {selectedVehicle === v.id && (
                      <div className="absolute top-3 right-3 w-6 h-6 bg-voyage-gold rounded-full flex items-center justify-center">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                    )}
                    <div className="w-full h-32 mb-4 overflow-hidden rounded-xl">
                      <img
                        src={v.image}
                        alt={v.name}
                        title={`${v.name} - ${v.type}`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform"
                      />
                    </div>
                    <h4 className="font-playfair text-lg font-bold text-night-blue">{v.name}</h4>
                    <p className="font-inter text-sm text-slate-500 mb-3">{v.type}</p>
                    <div className="flex items-center gap-4 text-sm font-inter text-slate-600">
                      <span className="flex items-center gap-1">
                        <i className="ri-user-line text-voyage-gold"></i>
                        {v.passengers} pers.
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-suitcase-line text-voyage-gold"></i>
                        {v.luggage} bag.
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line text-voyage-gold"></i>
                        {v.hourlyRate} €/h
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Étape 3 : Options */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-8 bg-voyage-gold rounded-full flex items-center justify-center text-white font-inter font-bold text-sm">3</div>
                <h3 className="font-playfair text-xl font-bold text-night-blue">Options supplémentaires</h3>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {supplements.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => toggleSupplement(s.id)}
                    className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-all cursor-pointer ${
                      selectedSupplements.includes(s.id)
                        ? 'border-voyage-gold bg-voyage-gold/5 shadow-sm'
                        : 'border-slate-200 bg-slate-50 hover:border-slate-300'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                      selectedSupplements.includes(s.id)
                        ? 'bg-voyage-gold text-white'
                        : 'bg-slate-200 text-slate-500'
                    }`}>
                      <i className={`${s.icon} text-base`}></i>
                    </div>
                    <div className="text-left flex-1">
                      <p className="font-inter text-sm font-medium text-night-blue">{s.label}</p>
                      <p className="font-inter text-xs text-slate-500">
                        {s.price > 0 ? `+${s.price} €` : 'Gratuit'}
                      </p>
                    </div>
                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
                      selectedSupplements.includes(s.id)
                        ? 'border-voyage-gold bg-voyage-gold'
                        : 'border-slate-300'
                    }`}>
                      {selectedSupplements.includes(s.id) && (
                        <i className="ri-check-line text-white text-xs"></i>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Bouton Simuler */}
            <motion.button
              onClick={handleSimulate}
              disabled={!matchedRoute}
              whileHover={{ scale: matchedRoute ? 1.02 : 1 }}
              whileTap={{ scale: matchedRoute ? 0.98 : 1 }}
              className="w-full py-4 bg-gradient-to-r from-voyage-gold to-amber-500 text-night-blue rounded-xl font-inter font-bold text-lg hover:shadow-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              <i className="ri-calculator-line text-xl mr-2"></i>
              Calculer mon prix
            </motion.button>
          </div>

          {/* Résultat */}
          <AnimatePresence>
            {showResult && matchedRoute && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <div className="bg-gradient-to-r from-night-blue to-slate-800 p-6 md:p-10">
                  <div className="grid md:grid-cols-3 gap-6 items-center">
                    {/* Récapitulatif */}
                    <div className="md:col-span-1">
                      <p className="font-inter text-xs uppercase tracking-widest text-slate-400 mb-3">Récapitulatif</p>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-white/80 font-inter text-sm">
                          <i className="ri-map-pin-line text-voyage-gold"></i>
                          {selectedDeparture}
                        </div>
                        <div className="flex items-center gap-2 text-white/80 font-inter text-sm">
                          <i className="ri-flag-line text-voyage-gold"></i>
                          {selectedDestination}
                        </div>
                        <div className="flex items-center gap-2 text-white/80 font-inter text-sm">
                          <i className="ri-car-line text-voyage-gold"></i>
                          {currentVehicle?.name}
                        </div>
                        <div className="flex items-center gap-2 text-white/80 font-inter text-sm">
                          <i className="ri-route-line text-voyage-gold"></i>
                          {matchedRoute.distanceKm} km • ~{matchedRoute.durationMin} min
                        </div>
                        {tripType === 'aller-retour' && (
                          <div className="flex items-center gap-2 text-green-400 font-inter text-sm">
                            <i className="ri-arrow-left-right-line"></i>
                            Aller-retour (-5%)
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Prix */}
                    <div className="md:col-span-1 text-center">
                      <p className="font-inter text-xs uppercase tracking-widest text-slate-400 mb-2">Prix estimé</p>
                      <motion.div
                        key={totalPrice}
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="font-playfair text-6xl md:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-voyage-gold to-amber-400"
                      >
                        {totalPrice} €
                      </motion.div>
                      <p className="font-inter text-xs text-slate-400 mt-2">Prix fixe tout compris</p>
                    </div>

                    {/* CTA */}
                    <div className="md:col-span-1 flex flex-col gap-3">
                      <a
                        href={generateWhatsAppLink()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 px-6 py-4 bg-[#25D366] text-white rounded-xl font-inter font-bold hover:bg-[#20BA5A] hover:scale-105 transition-all whitespace-nowrap cursor-pointer shadow-lg"
                      >
                        <i className="ri-whatsapp-line text-xl"></i>
                        Réserver sur WhatsApp
                      </a>
                      <a
                        href="/reservation"
                        className="flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-voyage-gold to-amber-500 text-night-blue rounded-xl font-inter font-bold hover:shadow-xl hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-calendar-check-line text-xl"></i>
                        Réserver en ligne
                      </a>
                      <a
                        href="tel:+33605606006"
                        className="flex items-center justify-center gap-2 px-6 py-3 border border-white/20 text-white rounded-xl font-inter font-medium hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-phone-line"></i>
                        +33 6 05 60 60 06
                      </a>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
